import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Users,
  CalendarDays,
  CheckSquare,
  MessageCircle,
  LayoutGrid,
  FileText,
  CreditCard,
  BarChart3,
  Settings as SettingsIcon,
  Shield,
  Home,
  Check,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MODULE_ICONS = {
  painel: Home,
  convidados: Users,
  mesas: LayoutGrid,
  checkin: CheckSquare,
  whatsapp: MessageCircle,
  agenda: CalendarDays,
  orcamentos: FileText,
  despesas: CreditCard,
  relatorios: BarChart3,
  configuracoes: SettingsIcon,
  usuarios: Shield,
};

const PRESETS = [
  { id: "admin", label: "Administrador", desc: "Acesso total — ex: cerimonialista" },
  { id: "editor", label: "Editor", desc: "Edita convidados, check-in, mesas, agenda" },
  { id: "viewer", label: "Visualizador", desc: "Apenas visualiza" },
  { id: "custom", label: "Personalizado", desc: "Defina ação por ação" },
];

let _metaCache = null;

function deepEqual(a, b) {
  return JSON.stringify(a) === JSON.stringify(b);
}

function detectPreset(perms, presets) {
  if (!perms || !presets) return "custom";
  for (const id of ["admin", "editor", "viewer"]) {
    if (deepEqual(perms, presets[id])) return id;
  }
  return "custom";
}

/**
 * PermissionEditor (premium)
 * props:
 *   value: permissions object (controlled). If null/undefined, uses internal default ('viewer' preset on mount).
 *   onChange(perms): receives the updated permission map.
 *   selectedPresetId, onPresetChange (optional, controlled).
 */
export default function PermissionEditor({
  value,
  onChange,
  selectedPresetId,
  onPresetChange,
  // Lista de módulos a OCULTAR completamente (ex.: ["receitas"] — acesso financeiro é exclusivo do proprietário).
  hideModules = [],
}) {
  const [meta, setMeta] = useState(_metaCache);
  const [loading, setLoading] = useState(!_metaCache);
  const [internalPreset, setInternalPreset] = useState("editor");

  useEffect(() => {
    if (_metaCache) {
      setMeta(_metaCache);
      return;
    }
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API}/me/permissions-meta`)
      .then((res) => {
        if (cancelled) return;
        _metaCache = res.data;
        setMeta(res.data);
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  // Initialize value from preset on first meta load if uncontrolled
  useEffect(() => {
    if (meta && value == null) {
      onChange?.(meta.presets.editor);
    }
  }, [meta, value, onChange]);

  const presetId = useMemo(() => {
    if (selectedPresetId) return selectedPresetId;
    if (!meta || !value) return internalPreset;
    return detectPreset(value, meta.presets);
  }, [selectedPresetId, meta, value, internalPreset]);

  const setPreset = (id) => {
    if (!meta) return;
    if (id !== "custom") {
      onChange?.(meta.presets[id]);
    } else if (!value) {
      onChange?.(meta.presets.viewer);
    }
    setInternalPreset(id);
    onPresetChange?.(id);
  };

  const toggleAction = (module, action) => {
    if (!meta || !value) return;
    const next = { ...value, [module]: { ...(value[module] || {}) } };
    next[module][action] = !next[module][action];
    // Auto-rule: se desligar 'view', desligar todas as ações desse módulo
    if (action === "view" && next[module][action] === false) {
      Object.keys(next[module]).forEach((a) => {
        next[module][a] = false;
      });
    }
    // Auto-rule: se ligar qualquer ação, ligar 'view' também
    if (action !== "view" && next[module][action] === true) {
      next[module].view = true;
    }
    onChange?.(next);
    setInternalPreset("custom");
    onPresetChange?.("custom");
  };

  const toggleAllInModule = (module, on) => {
    if (!meta || !value) return;
    const acts = meta.modules[module] || [];
    const next = { ...value, [module]: {} };
    acts.forEach((a) => {
      next[module][a] = !!on;
    });
    onChange?.(next);
    setInternalPreset("custom");
    onPresetChange?.("custom");
  };

  if (loading || !meta || !value) {
    return (
      <div className="space-y-3" data-testid="permission-editor-loading">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-zinc-50 rounded-xl h-20 animate-pulse" />
        ))}
      </div>
    );
  }

  const moduleEntries = Object.entries(meta.modules).filter(([key]) => !hideModules.includes(key));

  return (
    <div className="space-y-4" data-testid="permission-editor">
      {/* Preset selector */}
      <div className="grid grid-cols-2 gap-2">
        {PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => setPreset(p.id)}
            data-testid={`preset-${p.id}`}
            className={`text-left p-3 rounded-xl border transition-all ${
              presetId === p.id
                ? "border-zinc-900 bg-brand-deep text-white"
                : "border-zinc-200/60 bg-white hover:border-zinc-300"
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="text-[13.5px] font-semibold tracking-tight">{p.label}</span>
              {presetId === p.id && <Check size={14} />}
            </div>
            <p className={`text-[11px] mt-0.5 ${presetId === p.id ? "text-zinc-300" : "text-zinc-500"}`}>{p.desc}</p>
          </button>
        ))}
      </div>

      {/* Module cards */}
      <div className="space-y-2.5" data-testid="permission-editor-modules">
        {moduleEntries.map(([module, actions]) => {
          const Icon = MODULE_ICONS[module] || Shield;
          const moduleLabel = meta.module_labels[module] || module;
          const moduleVal = value[module] || {};
          const allOn = actions.every((a) => moduleVal[a]);
          const someOn = !allOn && actions.some((a) => moduleVal[a]);

          return (
            <details
              key={module}
              open={moduleVal.view || allOn}
              className="group bg-white rounded-xl border border-zinc-200/60 overflow-hidden"
              data-testid={`module-card-${module}`}
            >
              <summary className="list-none cursor-pointer px-4 py-3 flex items-center gap-3 select-none">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  allOn ? "bg-emerald-50 text-emerald-600" : someOn ? "bg-zinc-100 text-zinc-700" : "bg-zinc-50 text-zinc-400"
                }`}>
                  <Icon size={16} strokeWidth={1.6} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-semibold text-zinc-900 tracking-tight">{moduleLabel}</p>
                  <p className="text-[11.5px] text-zinc-500">
                    {allOn
                      ? "Acesso total"
                      : someOn
                      ? `${actions.filter((a) => moduleVal[a]).length}/${actions.length} ações`
                      : "Sem acesso"}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleAllInModule(module, !allOn);
                  }}
                  data-testid={`toggle-all-${module}`}
                  className={`text-[11px] px-2.5 py-1 rounded-md font-medium transition-colors ${
                    allOn
                      ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                      : "bg-zinc-100 text-zinc-700 hover:bg-zinc-200"
                  }`}
                >
                  {allOn ? "Tudo" : "Tudo"}
                </button>
              </summary>

              <div className="px-4 pb-3 pt-1 grid grid-cols-1 sm:grid-cols-2 gap-1.5 border-t border-zinc-100">
                {actions.map((action) => {
                  const checked = !!moduleVal[action];
                  const actionLabel = meta.action_labels[action] || action;
                  return (
                    <label
                      key={action}
                      className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg cursor-pointer transition-colors ${
                        checked ? "bg-zinc-50" : "hover:bg-zinc-50"
                      }`}
                      data-testid={`perm-${module}-${action}`}
                    >
                      <span
                        className={`w-4 h-4 rounded-[5px] flex items-center justify-center flex-shrink-0 transition-all ${
                          checked
                            ? "bg-brand-deep border border-zinc-900"
                            : "bg-white border border-zinc-300"
                        }`}
                      >
                        {checked && <Check size={11} className="text-white" strokeWidth={3} />}
                      </span>
                      <input
                        type="checkbox"
                        className="sr-only"
                        checked={checked}
                        onChange={() => toggleAction(module, action)}
                      />
                      <span className="text-[13px] text-zinc-700 select-none">{actionLabel}</span>
                    </label>
                  );
                })}
              </div>
            </details>
          );
        })}
      </div>
    </div>
  );
}
